import axios from "axios";
import { toast } from "react-toastify";

const API_URL = "/api";

// Configuration Axios avec Token
const axiosInstance = axios.create({
  baseURL: API_URL,
});

axiosInstance.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Intercepteur pour gérer les erreurs d'authentification
axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    if (
      error.response &&
      (error.response.status === 401 || error.response.status === 403)
    ) {
      // Token expiré ou invalide - ne pas rediriger si déjà sur la page de connexion,
      // la page de réinitialisation de mot de passe ou la page de callback Google
      const currentPath = window.location.pathname;
      if (
        currentPath !== "/login" &&
        !currentPath.startsWith("/reset-password") &&
        !currentPath.startsWith("/auth/callback") &&
        !currentPath.startsWith("/email-verification") // Ajout pour ne pas rediriger pendant la vérification
      ) {
        localStorage.removeItem("token");
        localStorage.removeItem("isAdmin");
        localStorage.removeItem("userName");
        localStorage.removeItem("profilePicture"); // Ajout pour l'image de profil Google

        toast.error("Votre session a expiré. Veuillez vous reconnecter.");
        window.location.href = "/login";
      }
    }
    return Promise.reject(error);
  }
);

// Fonction de déconnexion
export const logout = () => {
  localStorage.removeItem("token");
  localStorage.removeItem("isAdmin");
  localStorage.removeItem("userName");
  localStorage.removeItem("profilePicture"); // Ajout pour l'image de profil Google
  window.location.href = "/login";
};

// AUTHENTIFICATION
export const registerUser = (firstName, lastName, email, password) =>
  axiosInstance.post(`/auth/register`, {
    firstName,
    lastName,
    email,
    password,
  });

export const login = (email, password) =>
  axiosInstance.post(`/auth/login`, { email, password });

// Google OAuth - ceci est géré directement par redirection, pas besoin de fonction API spécifique
// La fonction est fournie pour cohérence d'API, mais redirige simplement vers l'endpoint OAuth
export const googleLogin = () => {
  window.location.href = `${API_URL}/auth/google`;
};

// Nouvelle fonction pour renvoyer l'email de vérification
export const resendVerificationEmail = (email) => {
  return axiosInstance.post(`/auth/resend-verification`, { email });
};

// NOUVELLE FONCTION: Vérifier le token d'email
export const verifyEmailToken = (token) => {
  return axiosInstance.post(`/auth/verify-email`, { token });
};

// Demande de réinitialisation de mot de passe
export const requestPasswordReset = (email) => {
  return axiosInstance.post(`/auth/forgot-password`, { email });
};

// Réinitialisation de mot de passe
export const resetPassword = (token, password) => {
  return axiosInstance.post(`/auth/reset-password/${token}`, { password });
};

// GESTION UTILISATEURS
export const getAllUsers = () => axiosInstance.get(`/users/all`);

// Récupérer le profil de l'utilisateur connecté (sans ID)
export const getCurrentUserProfile = () => axiosInstance.get(`/users/profile`);

// Récupérer le profil d'un utilisateur spécifique (admin)
export const getUserProfile = (id) => axiosInstance.get(`/users/profile/${id}`);

// Mettre à jour le profil d'un utilisateur spécifique (admin)
export const updateUser = (id, userData) =>
  axiosInstance.put(`/users/${id}`, userData);

// Mettre à jour son propre profil
export const updateCurrentUser = (userData) =>
  axiosInstance.put(`/users/profile`, userData);

export const deleteUser = (id) => axiosInstance.delete(`/users/${id}`);

export const createUser = (userData) =>
  axiosInstance.post(`/users/create`, userData);

// GESTION DES COMMANDES - UTILISATEUR
// Récupérer les commandes de l'utilisateur connecté
export const getMyOrders = () => axiosInstance.get(`/mollie/orders`);

// Récupérer une commande spécifique par ID
export const getOrderById = (orderId) =>
  axiosInstance.get(`/mollie/orders/${orderId}`);

// GESTION DES COMMANDES - ADMIN
// NOUVEAU: Récupérer toutes les commandes (admin)
export const getAllOrders = (params = {}) => {
  const queryString = new URLSearchParams(params).toString();
  return axiosInstance.get(
    `/mollie/admin/orders${queryString ? `?${queryString}` : ""}`
  );
};

// NOUVEAU: Récupérer les statistiques des commandes (admin)
export const getOrderStats = () => axiosInstance.get(`/mollie/admin/stats`);

// NOUVEAU: Mettre à jour l'état d'une commande (admin)
export const updateOrderStatus = (orderId, statusData) =>
  axiosInstance.put(`/mollie/admin/orders/${orderId}/status`, statusData);

// GESTION DES PAIEMENTS MOLLIE
// Créer un paiement
export const createPayment = (paymentData) =>
  axiosInstance.post(`/mollie/payments`, paymentData);

// Récupérer un paiement par ID
export const getPayment = (paymentId) =>
  axiosInstance.get(`/mollie/payments/${paymentId}`);

// Récupérer les méthodes de paiement
export const getPaymentMethods = (params = {}) =>
  axiosInstance.get(`/mollie/methods`, { params });

// UTILITAIRES POUR LES ÉTATS DE COMMANDE
export const ORDER_STATUS = {
  EN_ATTENTE_PAIEMENT: "en_attente_paiement",
  PAYEE: "payee",
  EN_PREPARATION: "en_preparation",
  EXPEDIEE: "expediee",
  LIVREE: "livree",
  ANNULEE: "annulee",
  RETOURNEE: "retournee",
};

export const ORDER_STATUS_LABELS = {
  [ORDER_STATUS.EN_ATTENTE_PAIEMENT]: "En attente de paiement",
  [ORDER_STATUS.PAYEE]: "Payée",
  [ORDER_STATUS.EN_PREPARATION]: "En préparation",
  [ORDER_STATUS.EXPEDIEE]: "Expédiée",
  [ORDER_STATUS.LIVREE]: "Livrée",
  [ORDER_STATUS.ANNULEE]: "Annulée",
  [ORDER_STATUS.RETOURNEE]: "Retournée",
};

// NOUVEAU: Configuration pour les filtres admin
export const ORDER_FILTERS = {
  ALL: "all",
  PENDING: "pending",
  SHIPPED: "shipped",
  PROBLEMS: "problems",
};

export const ORDER_FILTER_LABELS = {
  [ORDER_FILTERS.ALL]: "Toutes",
  [ORDER_FILTERS.PENDING]: "En cours",
  [ORDER_FILTERS.SHIPPED]: "Expédiées",
  [ORDER_FILTERS.PROBLEMS]: "Problèmes",
};

// Fonction helper pour obtenir le label d'un statut
export const getOrderStatusLabel = (status) => {
  return ORDER_STATUS_LABELS[status] || status;
};

// NOUVEAU: Fonction helper pour obtenir la configuration d'un statut
export const getOrderStatusConfig = (status) => {
  const configs = {
    [ORDER_STATUS.EN_ATTENTE_PAIEMENT]: {
      label: ORDER_STATUS_LABELS[ORDER_STATUS.EN_ATTENTE_PAIEMENT],
      icon: "bi-clock-fill",
      color: "#ffc107",
      bgColor: "#fff3cd",
      textColor: "#856404",
      progress: 10,
    },
    [ORDER_STATUS.PAYEE]: {
      label: ORDER_STATUS_LABELS[ORDER_STATUS.PAYEE],
      icon: "bi-check-circle-fill",
      color: "#28a745",
      bgColor: "#d4edda",
      textColor: "#155724",
      progress: 25,
    },
    [ORDER_STATUS.EN_PREPARATION]: {
      label: ORDER_STATUS_LABELS[ORDER_STATUS.EN_PREPARATION],
      icon: "bi-box-seam-fill",
      color: "#17a2b8",
      bgColor: "#d1ecf1",
      textColor: "#0c5460",
      progress: 50,
    },
    [ORDER_STATUS.EXPEDIEE]: {
      label: ORDER_STATUS_LABELS[ORDER_STATUS.EXPEDIEE],
      icon: "bi-truck",
      color: "#6f42c1",
      bgColor: "#e2d9f3",
      textColor: "#493169",
      progress: 75,
    },
    [ORDER_STATUS.LIVREE]: {
      label: ORDER_STATUS_LABELS[ORDER_STATUS.LIVREE],
      icon: "bi-house-check-fill",
      color: "#28a745",
      bgColor: "#d4edda",
      textColor: "#155724",
      progress: 100,
    },
    [ORDER_STATUS.ANNULEE]: {
      label: ORDER_STATUS_LABELS[ORDER_STATUS.ANNULEE],
      icon: "bi-x-circle-fill",
      color: "#dc3545",
      bgColor: "#f8d7da",
      textColor: "#721c24",
      progress: 0,
    },
    [ORDER_STATUS.RETOURNEE]: {
      label: ORDER_STATUS_LABELS[ORDER_STATUS.RETOURNEE],
      icon: "bi-arrow-return-left",
      color: "#fd7e14",
      bgColor: "#ffeaa7",
      textColor: "#8a4a00",
      progress: 0,
    },
  };

  return configs[status] || configs[ORDER_STATUS.EN_ATTENTE_PAIEMENT];
};

// NOUVEAU: Fonction helper pour formater les dates
export const formatDate = (dateString, options = {}) => {
  if (!dateString) return "N/A";

  const defaultOptions = {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    ...options,
  };

  return new Date(dateString).toLocaleDateString("fr-FR", defaultOptions);
};

// NOUVEAU: Fonction helper pour calculer le temps écoulé
export const getTimeAgo = (dateString) => {
  if (!dateString) return "N/A";

  const now = new Date();
  const date = new Date(dateString);
  const diffInSeconds = Math.floor((now - date) / 1000);

  if (diffInSeconds < 60) return "À l'instant";
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} min`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} h`;
  if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 86400)} j`;

  return formatDate(dateString, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};
